import Phaser from 'phaser';
import { BootScene } from './scenes/BootScene';
import { MenuScene } from './scenes/MenuScene';
import { ChapterScene } from './scenes/ChapterScene';
import { GameScene } from './scenes/GameScene';
import { ResultScene } from './scenes/ResultScene';
import { AdScene } from './scenes/AdScene';
import { installFontFloor } from './render/theme';
import { installBackButton } from './back-button';

installFontFloor();

/**
 * Fit-width scaling so the game fills ANY phone aspect ratio with no black
 * bars. We keep the design 1080px wide (all UI is authored for that width)
 * but set the base HEIGHT to match the real screen aspect. Because the base
 * aspect then equals the screen aspect, Phaser.Scale.FIT scales it up to fill
 * the screen exactly — no letterboxing. Taller phones simply get more vertical
 * space (every scene reads this.scale.height, so they adapt). The min clamp
 * keeps the portrait layout sane in a landscape desktop browser.
 */
const DESIGN_W = 1080;
const MIN_H = 1920;

function designSize(): { width: number; height: number } {
  const w = window.innerWidth || DESIGN_W;
  const h = window.innerHeight || MIN_H;
  const height = Math.max(MIN_H, Math.round(DESIGN_W * (h / w)));
  return { width: DESIGN_W, height };
}

const size = designSize();

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game',
  backgroundColor: '#05060d',
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: size.width,
    height: size.height,
  },
  input: {
    activePointers: 3,
  },
  render: {
    antialias: true,
    pixelArt: false,
  },
  scene: [BootScene, MenuScene, ChapterScene, GameScene, ResultScene, AdScene],
};

const game = new Phaser.Game(config);
(window as unknown as { game: Phaser.Game }).game = game;
installBackButton(game as unknown as Parameters<typeof installBackButton>[0]);

// Re-fit on rotation / window resize (desktop). Recompute the base height so
// it keeps matching the new aspect, then restart the active scene so its
// layout reflows to the new dimensions.
let resizeTimer: number | undefined;
window.addEventListener('resize', () => {
  window.clearTimeout(resizeTimer);
  resizeTimer = window.setTimeout(() => {
    const next = designSize();
    game.scale.setGameSize(next.width, next.height);
    const active = game.scene.getScenes(true)[0];
    if (active) game.scene.getScene(active.scene.key).scene.restart();
  }, 150);
});
